<template>
  <div
    v-if="props.product"
    class="card h-100 border-2"
    :class="{
      'bg-white opacity-50 pe-none': props.isDisabled,
      'border-primary': props.isRecommended && !props.isDisabled,
    }"
    :data-testid="`product-card-${props.product.id}`"
    :data-recommended="props.isRecommended ? 'true' : undefined"
    @click="selectProduct">
    <div class="product-content">
      <div class="d-flex gap-3 justify-content-between align-items-center">
        <div class="avatar" :style="{ backgroundColor: props.product.image_background || undefined }">
          <img
            :src="props.product.image?.data?.proxy.url_preview"
            alt="Product Image"
            v-if="props.product.image?.data?.proxy.url_preview" />
        </div>
        <div class="d-block text-right ms-auto">
          <h2 class="text-truncate product-value-label" v-html="productLabel"></h2>
          <h3 class="text-truncate" v-if="props.product.allocation_sublabel">
            {{ props.product.allocation_sublabel }}
          </h3>
        </div>
      </div>
      <div class="mt-4">
        <div class="d-flex align-items-start justify-content-between gap-2">
          <h3 class="fw-bold mb-0">{{ props.product.name }}</h3>
          <span
            v-if="props.isRecommended && !props.isDisabled"
            class="status status-blue flex-shrink-0"
            data-testid="product-recommended-badge"
            >Recommended</span
          >
        </div>
        <p class="minor-text">{{ props.product.description }}</p>
        <p
          v-if="props.isRecommended && !props.isDisabled && props.recommendationReason"
          class="minor-text text-muted mb-0">
          {{ props.recommendationReason }}
        </p>
        <p class="minor-text fw-medium mt-2">Eckdaten</p>
        <div class="d-flex w-100 gap-2 flex-wrap justify-content-between align-items-center">
          <p class="minor-text fw-medium m-0">{{ Object.values(props.product.key_features).join(' / ') }}</p>
          <div class="badges">
            <ContingentProductRefillBadge :product="props.product" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import type { IProduct } from '@/types/product';
import { computed } from 'vue';
import ContingentProductRefillBadge from './ContingentProductRefillBadge.vue';

const props = withDefaults(
  defineProps<{
    product: IProduct | undefined;
    isDisabled: boolean;
    isRecommended?: boolean;
    recommendationReason?: string;
  }>(),
  {
    isDisabled: false,
    isRecommended: false,
    recommendationReason: '',
  }
);

const emit = defineEmits<{
  (e: 'select', product: IProduct): void;
}>();

const productLabel = computed(() => {
  const label = props.product?.allocation_label.replace(/\*\*\*(.*?)\*\*\*/g, '<span class="text-muted">$1</span>');
  return label || '';
});

function selectProduct() {
  emit('select', props.product!);
}
</script>
<style scoped>
.minor-text {
  font-size: 13px;
}

.product-value-label {
  font-size: 20px;
  font-weight: bold;
}

.product-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 300px;
  padding: 20px;
}

.card:hover {
  cursor: pointer;
  border-color: var(--tblr-primary);
}

.avatar {
  flex: 0 0 70px;
  width: 70px;
  height: 70px;
}

.avatar img {
  background-size: contain;
  flex: 0 1 40px;
  max-width: 40px;
  max-height: 40px;
}
</style>
